import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ProfileTypesTab — admin CRUD for user profile types (e.g. Maître d'ouvrage, Architecte)
const ProfileTypesTab = () => {
  const [profileTypes, setProfileTypes] = useState([]);
  const [showProfileTypeDialog, setShowProfileTypeDialog] = useState(false);
  const [profileTypeForm, setProfileTypeForm] = useState({ name: '', description: '' });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/profile-types`, { withCredentials: true });
      setProfileTypes(r.data);
    } catch {
      toast.error('Erreur de chargement');
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreateProfileType = async () => {
    try {
      await axios.post(`${API}/admin/profile-types`, profileTypeForm, { withCredentials: true });
      toast.success('Type de profil créé');
      setShowProfileTypeDialog(false);
      setProfileTypeForm({ name: '', description: '' });
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDeleteProfileType = async (typeId) => {
    try {
      await axios.delete(`${API}/admin/profile-types/${typeId}`, { withCredentials: true });
      setProfileTypes(profileTypes.filter(t => t.type_id !== typeId));
      toast.success('Type supprimé');
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium">Types de profil</h2>
        <Button onClick={() => { setProfileTypeForm({ name: '', description: '' }); setShowProfileTypeDialog(true); }} data-testid="add-profile-type-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau type
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {profileTypes.map(pt => (
          <div key={pt.type_id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900">{pt.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{pt.description}</p>
            </div>
            <Button
              variant="ghost" size="icon"
              className="text-red-500 hover:bg-red-50 h-8 w-8 shrink-0"
              onClick={() => handleDeleteProfileType(pt.type_id)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={showProfileTypeDialog} onOpenChange={setShowProfileTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau type de profil</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Nom *</label>
              <Input value={profileTypeForm.name} onChange={(e) => setProfileTypeForm({...profileTypeForm, name: e.target.value})} placeholder="Ex: Maître d'ouvrage" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Description</label>
              <Input value={profileTypeForm.description} onChange={(e) => setProfileTypeForm({...profileTypeForm, description: e.target.value})} placeholder="Description du type" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileTypeDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateProfileType} disabled={!profileTypeForm.name} className="bg-blue-600 hover:bg-blue-700">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileTypesTab;
